import type {
  CreateFieldRequest,
  CreateItemTypeRequest,
  FieldDefinition,
  ItemType,
  ItemTypeListItem,
  ReorderFieldsRequest,
  UpdateFieldRequest,
  UpdateItemTypeRequest,
} from '@lens/shared';
import { apiRequest } from '../../shared/api/client.ts';

/**
 * HTTP-обёртки над эндпоинтами `/api/spaces/:id/item-types/*`.
 * M4. См. docs/api-design.md §4.5.
 */

const base = (spaceId: string) => `/api/spaces/${spaceId}/item-types`;
const typeBase = (spaceId: string, itemTypeId: string) =>
  `${base(spaceId)}/${itemTypeId}`;
const fieldBase = (spaceId: string, itemTypeId: string) =>
  `${typeBase(spaceId, itemTypeId)}/fields`;

export async function listItemTypes(spaceId: string): Promise<ItemTypeListItem[]> {
  return apiRequest<ItemTypeListItem[]>(base(spaceId), { method: 'GET' });
}

export async function getItemType(spaceId: string, itemTypeId: string): Promise<ItemType> {
  return apiRequest<ItemType>(typeBase(spaceId, itemTypeId), { method: 'GET' });
}

export async function createItemType(
  spaceId: string,
  data: CreateItemTypeRequest,
): Promise<ItemType> {
  return apiRequest<ItemType>(base(spaceId), { method: 'POST', body: data });
}

export async function updateItemType(
  spaceId: string,
  itemTypeId: string,
  data: UpdateItemTypeRequest,
): Promise<ItemType> {
  return apiRequest<ItemType>(typeBase(spaceId, itemTypeId), { method: 'PATCH', body: data });
}

export async function deleteItemType(spaceId: string, itemTypeId: string): Promise<void> {
  await apiRequest<void>(typeBase(spaceId, itemTypeId), { method: 'DELETE' });
}

export async function duplicateItemType(
  spaceId: string,
  itemTypeId: string,
): Promise<ItemType> {
  return apiRequest<ItemType>(`${typeBase(spaceId, itemTypeId)}/duplicate`, {
    method: 'POST',
  });
}

export async function listFields(
  spaceId: string,
  itemTypeId: string,
): Promise<FieldDefinition[]> {
  return apiRequest<FieldDefinition[]>(fieldBase(spaceId, itemTypeId), { method: 'GET' });
}

export async function createField(
  spaceId: string,
  itemTypeId: string,
  data: CreateFieldRequest,
): Promise<FieldDefinition> {
  return apiRequest<FieldDefinition>(fieldBase(spaceId, itemTypeId), {
    method: 'POST',
    body: data,
  });
}

export async function updateField(
  spaceId: string,
  itemTypeId: string,
  fieldId: string,
  data: UpdateFieldRequest,
): Promise<FieldDefinition> {
  return apiRequest<FieldDefinition>(`${fieldBase(spaceId, itemTypeId)}/${fieldId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteField(
  spaceId: string,
  itemTypeId: string,
  fieldId: string,
): Promise<void> {
  await apiRequest<void>(`${fieldBase(spaceId, itemTypeId)}/${fieldId}`, { method: 'DELETE' });
}

export async function reorderFields(
  spaceId: string,
  itemTypeId: string,
  data: ReorderFieldsRequest,
): Promise<FieldDefinition[]> {
  return apiRequest<FieldDefinition[]>(`${fieldBase(spaceId, itemTypeId)}/reorder`, {
    method: 'POST',
    body: data,
  });
}
